import { Dexie, type EntityTable } from "dexie";
import { Note, UserSettings, Folder } from "../../shared/types";
import { isContinuousIntegration } from "../utils/environment";
import { developCacheDatabaseName } from "./developCacheScope";

type User = {
  id: string;
  data: {
    settings: UserSettings;
  };
  isDirty: number;
};

type AudioChunk = {
  id: string;
  audioId: string;
  chunkIndex: number;
  duration: number;
  blob: Blob;
  isDirty: number;
};

type kv = {
  key: string;
  value: unknown;
};

// eslint-disable-next-line
const shouldUseMock = process.env.JEST_WORKER_ID !== undefined || isContinuousIntegration;

const db = new Dexie(developCacheDatabaseName()) as Dexie & {
  notes: EntityTable<{ id: string; data: Note; isDirty: number }, "id">;
  kv: EntityTable<kv, "key">;
  users: EntityTable<User, "id">;
  folders: EntityTable<{ id: string; data: Folder; isDirty: number }, "id">;
  audioChunks: EntityTable<AudioChunk, "id">;
};

db.version(2).stores({
  notes: "id, data, authorId, isDirty",
  folders: "id, data, isDirty",
  users: "id, data, isDirty",
  audioChunks: "id, [audioId+chunkIndex], duration, blob, isDirty",
  kv: "key, value",
});

const upsertNotes = async (notes: Note[], isDirty: boolean) => {
  await db.notes.bulkPut(notes.map((note) => ({ id: note.id, data: note, isDirty: isDirty ? 1 : 0 })));
};

const markDirtyNotes = async (ids: string[], isDirty: boolean) => {
  await db.notes
    .where("id")
    .anyOf(ids)
    .modify((note) => {
      note.isDirty = isDirty ? 1 : 0;
    });
};

const getNote = async (id: string): Promise<Note | null> => {
  const note = await db.notes.get(id);
  if (!note) {
    return null;
  }
  return note.data;
};

const getAllNotes = async (): Promise<Note[]> => {
  return await db.notes.toArray().then((notes) => notes.map((note) => note.data));
};

const getDirtyNoteIds = async (): Promise<string[]> => {
  return (await db.notes.where("isDirty").equals(1).primaryKeys()) as string[];
};

const upsertFolders = async (folders: Folder[], isDirty: boolean) => {
  // TODO: Add authorId to folders
  await db.folders.bulkPut(folders.map((folder) => ({ id: folder.id, data: folder, isDirty: isDirty ? 1 : 0 })));
};

const getFolder = async (id: string): Promise<Folder | null> => {
  const folder = await db.folders.get(id);
  return folder?.data ?? null;
};

const getAllFolders = async (): Promise<Folder[]> => {
  return await db.folders.toArray().then((folders) => folders.map((folder) => folder.data));
};

const markDirtyFolders = async (ids: string[], isDirty: boolean) => {
  await db.folders
    .where("id")
    .anyOf(ids)
    .modify((folder) => {
      folder.isDirty = isDirty ? 1 : 0;
    });
};

const getDirtyFolderIds = async (): Promise<string[]> => {
  return (await db.folders.where("isDirty").equals(1).primaryKeys()) as string[];
};

const upsertUser = async (user: User) => {
  const userId = "1";
  await db.users.put({ id: userId, data: { settings: user.data.settings }, isDirty: user.isDirty ? 1 : 0 });
};

const getUser = async (id: string): Promise<User | null> => {
  const userId = "1";
  const user = await db.users.get(userId);
  if (!user) {
    return null;
  }
  return {
    id: user.id,
    data: { settings: user.data.settings },
    isDirty: user.isDirty,
  };
};

const getKv = async (key: string): Promise<kv["value"] | null> => {
  const kv = await db.kv.get(key);
  if (!kv) {
    return null;
  }
  return kv.value;
};

const setKv = async (key: string, value: kv["value"]) => {
  await db.kv.put({ key, value });
};

const removeKv = async (key: string) => {
  await db.kv.delete(key);
};

const insertAudioChunk = async (audioChunk: AudioChunk) => {
  await db.audioChunks.put(audioChunk);
};

const updateAudioChunk = async (update: Pick<AudioChunk, "id" | "isDirty">) => {
  await db.audioChunks.update(update.id, { ...update, isDirty: update.isDirty ? 1 : 0 });
};

const getAudioChunk = async (id: string): Promise<AudioChunk | null> => {
  const audioChunk = await db.audioChunks.get(id);
  return audioChunk ?? null;
};

const getAllAudioChunksByAudioId = async (audioId: string): Promise<AudioChunk[]> => {
  return (await db.audioChunks.where("audioId").equals(audioId).sortBy("chunkIndex")) ?? [];
};

const getAllDirtyAudioChunks = async (): Promise<AudioChunk[]> => {
  return (await db.audioChunks.where("isDirty").equals(1).toArray()) ?? [];
};

const deleteAllAudioChunksByAudioId = async (audioId: string) => {
  await db.audioChunks.where("audioId").equals(audioId).delete();
};

const getUploadedAudioIds = async (): Promise<string[]> => {
  const allAudioIds = new Set<string>();
  const dirtyAudioIds = new Set<string>();
  await db.audioChunks.each((chunk) => {
    allAudioIds.add(chunk.audioId);
  });
  await db.audioChunks
    .where("isDirty")
    .equals(1)
    .each((chunk) => {
      dirtyAudioIds.add(chunk.audioId);
    });

  return Array.from(allAudioIds).filter((id) => !dirtyAudioIds.has(id));
};

const mockWebDB = {
  notes: {
    upsert: (_notes: Note[], _isDirty: boolean) => Promise.resolve(),
    get: (_id: string): Promise<Note | null> => Promise.resolve(null),
    getAll: (): Promise<Note[]> => Promise.resolve([]),

    getDirtyIds: (): Promise<string[]> => Promise.resolve([]),

    markDirty: (_ids: string[], _isDirty: boolean) => Promise.resolve(),
  },

  folders: {
    upsert: (_folders: Folder[], _isDirty: boolean) => Promise.resolve(),

    get: (_id: string): Promise<Folder | null> => Promise.resolve(null),

    getAll: (): Promise<Folder[]> => Promise.resolve([]),

    getDirtyIds: (): Promise<string[]> => Promise.resolve([]),

    markDirty: (_ids: string[], _isDirty: boolean) => Promise.resolve(),
  },

  users: {
    upsert: (_user: User) => Promise.resolve(),

    get: (_id: string): Promise<User | null> => Promise.resolve(null),
  },

  kv: {
    set: (_key: string, _value: kv["value"]) => Promise.resolve(),

    get: (_key: string): Promise<kv["value"] | null> => Promise.resolve(null),

    remove: (_key: string) => Promise.resolve(),
  },

  audioChunks: {
    insert: (_chunk: AudioChunk) => Promise.resolve(),

    update: (_update: Pick<AudioChunk, "id" | "isDirty">) => Promise.resolve(),

    get: (_id: string): Promise<AudioChunk | null> => Promise.resolve(null),

    getAllByAudioId: (_audioId: string): Promise<AudioChunk[]> => Promise.resolve([]),

    getAllDirty: (): Promise<AudioChunk[]> => Promise.resolve([]),

    deleteAllByAudioId: (_audioId: string) => Promise.resolve(),

    getUploadedAudioIds: (): Promise<string[]> => Promise.resolve([]),
  },
  drop: () => Promise.resolve(),
  clear: () => Promise.resolve(),
};

export const webDB = shouldUseMock
  ? mockWebDB
  : {
      notes: {
        upsert: upsertNotes,
        get: getNote,
        getAll: getAllNotes,
        getDirtyIds: getDirtyNoteIds,
        markDirty: markDirtyNotes,
      },
      folders: {
        upsert: upsertFolders,
        get: getFolder,
        getAll: getAllFolders,
        getDirtyIds: getDirtyFolderIds,
        markDirty: markDirtyFolders,
      },
      users: {
        upsert: upsertUser,
        get: getUser,
      },
      kv: {
        set: setKv,
        get: getKv,
        remove: removeKv,
      },
      audioChunks: {
        insert: insertAudioChunk,
        update: updateAudioChunk,
        get: getAudioChunk,
        getAllByAudioId: getAllAudioChunksByAudioId,
        getAllDirty: getAllDirtyAudioChunks,
        deleteAllByAudioId: deleteAllAudioChunksByAudioId,
        getUploadedAudioIds: getUploadedAudioIds,
      },
      drop: async () => {
        try {
          await db.delete({ disableAutoOpen: false });
        } catch (error) {
          console.error("Error clearing database", error);
        }
      },
      clear: async () => {
        await db.notes.clear();
        await db.folders.clear();
        await db.users.clear();
        await db.kv.clear();
        await db.audioChunks.clear();
      },
    };
